import React, { forwardRef, useEffect, useState } from 'react';
import { Dropzone, IMAGE_MIME_TYPE } from '@mantine/dropzone';
import {
  Flex,
  Group,
  Image,
  LoadingOverlay,
  Modal,
  Stack,
  Text
} from '@mantine/core';
import { DropzoneInputProps } from '.';
import { HiOutlineCloudUpload, HiOutlineExclamation } from 'react-icons/hi';
import './styles.scss';
import * as Unicons from '@iconscout/react-unicons';
import Button from '../Button';
import { useDisclosure } from '@mantine/hooks';
import { getSharepointCdnFile } from '../../../utilities';
import dayjs from 'dayjs';
import id from 'dayjs/locale/id';

export const IMAGE_TYPE = ['png', 'jpg', 'jpeg', 'gif'];
export const FILE_TYPE = ['pdf'];

const DropzoneInput = forwardRef<HTMLDivElement, DropzoneInputProps>(
  (props, ref) => {
    const {
      label,
      withAsterik,
      error,
      defaultValue,
      value,
      loading = false,
      errorUpload = false
    } = props;
    const [preview, setPreview] = useState<string | null>(null);
    const [opened, { toggle }] = useDisclosure(false);

    useEffect(() => {
      if ((defaultValue && !preview) || (value && !preview)) {
        setPreview(getSharepointCdnFile(defaultValue || value || ''));
      }
    }, [defaultValue, preview, value]);

    useEffect(() => {
      if (errorUpload) {
        setPreview(null);
      }
    }, [errorUpload]);

    const handleDrop = (files: File[]) => {
      const file = files[0];
      if (file && IMAGE_TYPE.includes(file.type.split('/')[1])) {
        const reader = new FileReader();
        reader.onload = () => {
          setPreview(reader.result as string);
          props.onHandleDrop &&
            props.onHandleDrop(files, reader.result.toString().split(',')[1]);
        };
        reader.readAsDataURL(file);
      }
    };

    const handleDownload = () => {
      if (preview.startsWith('data:image')) {
        const link = document.createElement('a');
        link.href = preview!;
        link.download = `preview-image-${dayjs()
          .locale(id)
          .toLocaleString()}.jpg`;
        link.click();
      } else {
        window.open(preview, '_blank');
      }
    };

    const handleRemove = () => {
      props.onHandleDrop && props.onHandleDrop([], '');
      setPreview(null);
    };

    return (
      <>
        <Stack gap={8} mt={6}>
          <Text className="text-xs-semibold color-secondary">
            {label} {withAsterik && <span className="text-red-60">*</span>}
          </Text>
          {!loading && preview ? (
            <Flex
              className="preview_container"
              direction={'column'}
              justify={'center'}
              align={'center'}
              gap={16}
            >
              <img
                src={preview}
                alt="Preview"
                className="preview_container__image_preview"
              />

              <Group>
                <Button
                  variant="error"
                  leftSection={<Unicons.UilTrashAlt size={24} />}
                  size="sm"
                  onClick={handleRemove}
                >
                  Remove
                </Button>
                <Button
                  variant="soft"
                  leftSection={<Unicons.UilEye size={24} />}
                  size="sm"
                  onClick={toggle}
                >
                  Preview
                </Button>
                <Button
                  variant="soft"
                  leftSection={<Unicons.UilCloudDownload size={24} />}
                  size="sm"
                  onClick={handleDownload}
                >
                  Download
                </Button>
              </Group>
            </Flex>
          ) : (
            <Dropzone
              onDrop={handleDrop}
              onReject={(files) => console.log('rejected files', files)}
              maxSize={2 * 1024 ** 2}
              accept={IMAGE_MIME_TYPE}
              {...props}
              ref={ref}
            >
              <Group
                justify="center"
                gap="xl"
                mih={220}
                style={{ pointerEvents: 'none' }}
              >
                {!loading && preview ? (
                  <img
                    src={preview}
                    alt="Preview"
                    style={{ maxWidth: '100%' }}
                  />
                ) : (
                  <>
                    <Dropzone.Accept>
                      <HiOutlineCloudUpload size={24} />
                    </Dropzone.Accept>
                    <Dropzone.Reject>
                      <HiOutlineExclamation size={24} />
                    </Dropzone.Reject>
                    <Dropzone.Idle>
                      <Unicons.UilImagePlus
                        size={24}
                        className="text-placeholder"
                      />
                    </Dropzone.Idle>
                    <LoadingOverlay visible={loading} />
                  </>
                )}

                <div>
                  <Text inline className="text-md-semibold text-placeholder">
                    Drag images or click to select files
                  </Text>
                  <Text size="sm" className="text-placeholder" inline mt={7}>
                    Attach .{IMAGE_TYPE.join(' .')} files with max. 2mb
                  </Text>
                </div>
              </Group>
            </Dropzone>
          )}
          {error && (
            <Text fz={12} c="red" mt={-8}>
              {error}
            </Text>
          )}
        </Stack>

        <Modal opened={opened} onClose={toggle} title="Preview">
          <Image src={preview} alt="Preview" style={{ maxWidth: '100%' }} />
        </Modal>
      </>
    );
  }
);

export default DropzoneInput;
